/**
 * Ted #35 (his Item 33; #1190 story 35.3, #948 story 1) — "Pricing
 * agreements" on the customer profile.
 *
 * Ted, 2026-10-06: "Alpin and Drew come Tuesdays and Thursdays and are at $70
 * rather than $85 in Gingr. Vicki handled those settings, so I'm not sure how
 * she set them up. Can we save a rate in each client's profile so staff can
 * see and apply their agreed pricing without changing the standard prices?"
 *
 * The owner saves the deal ONCE here; the settle dialog offers it at checkout
 * with one tap. The standard price stays beside it on every row so nobody has
 * to remember which number is the exception.
 *
 * Design Philosophy served:
 *   1 one glance, one truth — one row per deal, the standard price beside it;
 *   2 the next action is obvious — "Add agreed price" on an empty card, the
 *     empty state says what this is for;
 *   5 never lie about data — loading, error-with-retry, empty, ready and
 *     saving are five different renders; a manager sees the rows read-only
 *     WITH the plain reason, never a dead button;
 *   7 built for hands — every control is a ≥44px row; no hover affordances.
 *
 * Permissions are a UX gate only: writes are `business-config.pricing:write:all`
 * (owner) on the server; everyone with `customers:read:all` may read.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  adminPaymentsApi,
  fetchServiceTypes,
  readSession,
  humanizeApiError,
  type AdminRateAgreement,
  type AdminRateAgreementBasis,
  type AdminRateAgreementInput,
  type ServiceTypeSummary,
} from '../../lib/api.js';
import { agreementRowLabel, basisLabel, formatMoney, WEEKDAY_SHORT } from './rate-agreement-copy.js';
import './RateAgreementsCard.css';

export interface RateAgreementsCardProps {
  readonly apiUrl: string;
  readonly customerId: string;
  /** Owner-only (`business-config.pricing:write:all`). Others read the rows with the reason. */
  readonly canWrite: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; agreements: AdminRateAgreement[]; serviceTypes: ServiceTypeSummary[] };

interface FormState {
  /** null = adding; an id = editing that row. */
  editingId: string | null;
  serviceTypeId: string;
  /** Dollars as typed ("70" / "70.00"). */
  price: string;
  basis: AdminRateAgreementBasis;
  /** "" = any number of dogs. */
  dogCount: string;
  weekdays: number[];
  note: string;
}

const EMPTY_FORM = (serviceTypeId = ''): FormState => ({
  editingId: null,
  serviceTypeId,
  price: '',
  basis: 'per_household_day',
  dogCount: '',
  weekdays: [],
  note: '',
});

function formFromAgreement(a: AdminRateAgreement): FormState {
  return {
    editingId: a.id,
    serviceTypeId: a.serviceTypeId,
    price: (a.priceCents / 100).toFixed(2),
    basis: a.basis,
    dogCount: a.dogCount === null ? '' : String(a.dogCount),
    weekdays: [...a.weekdays],
    note: a.note,
  };
}

/** Parse-don't-validate at the form boundary: null = not ready to send. */
function toInput(f: FormState): AdminRateAgreementInput | null {
  const dollars = Number(f.price);
  if (!f.serviceTypeId || !Number.isFinite(dollars) || dollars < 0) return null;
  const dogCount = f.dogCount.trim() === '' ? null : Number(f.dogCount);
  if (dogCount !== null && (!Number.isInteger(dogCount) || dogCount < 1)) return null;
  const note = f.note.trim();
  if (note.length < 2) return null;
  return {
    serviceTypeId: f.serviceTypeId,
    priceCents: Math.round(dollars * 100),
    basis: f.basis,
    dogCount,
    weekdays: [...new Set(f.weekdays)].sort((a, b) => a - b),
    note,
  };
}

export function RateAgreementsCard({ apiUrl, customerId, canWrite }: RateAgreementsCardProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** The row whose Remove is awaiting a second tap (the counter test: no accidental removal). */
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const { token } = readSession();
    const res = await adminPaymentsApi.listRateAgreements(apiUrl, customerId);
    if (res.error || !res.data) {
      setState({ kind: 'error', message: humanizeApiError(res.error ?? null) });
      return;
    }
    // The service picker needs the catalogue only when this person may save.
    // Read it alongside, but a catalogue failure must not hide the rows.
    let serviceTypes: ServiceTypeSummary[] = [];
    if (canWrite && token) {
      try {
        serviceTypes = (await fetchServiceTypes(apiUrl, token)).filter((s) => !s.leg_only);
      } catch {
        serviceTypes = [];
      }
    }
    setState({ kind: 'ready', agreements: Array.isArray(res.data.agreements) ? res.data.agreements : [], serviceTypes });
  }, [apiUrl, customerId, canWrite]);

  useEffect(() => {
    void load();
  }, [load]);

  const openAdd = (): void => {
    if (state.kind !== 'ready') return;
    setFormError(null);
    setForm(EMPTY_FORM(state.serviceTypes[0]?.id ?? ''));
  };
  const openEdit = (a: AdminRateAgreement): void => {
    setFormError(null);
    setConfirmRemoveId(null);
    setForm(formFromAgreement(a));
  };
  const closeForm = (): void => {
    setForm(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form || state.kind !== 'ready') return;
    const input = toInput(form);
    if (!input) return;
    setSaving(true);
    setFormError(null);
    const res = form.editingId
      ? await adminPaymentsApi.updateRateAgreement(apiUrl, customerId, form.editingId, input)
      : await adminPaymentsApi.createRateAgreement(apiUrl, customerId, input);
    setSaving(false);
    if (res.error || !res.data?.agreement) {
      setFormError(humanizeApiError(res.error ?? null));
      return;
    }
    const saved = res.data.agreement;
    setState((current) => {
      if (current.kind !== 'ready') return current;
      const others = current.agreements.filter((a) => a.id !== saved.id);
      return { ...current, agreements: [saved, ...others] };
    });
    setForm(null);
  };

  const handleRemove = async (a: AdminRateAgreement): Promise<void> => {
    setSaving(true);
    setRowError(null);
    const res = await adminPaymentsApi.removeRateAgreement(apiUrl, customerId, a.id);
    setSaving(false);
    setConfirmRemoveId(null);
    if (res.error) {
      setRowError(humanizeApiError(res.error));
      return;
    }
    setState((current) =>
      current.kind === 'ready' ? { ...current, agreements: current.agreements.filter((x) => x.id !== a.id) } : current,
    );
  };

  const toggleWeekday = (d: number): void => {
    setForm((f) => {
      if (!f) return f;
      const has = f.weekdays.includes(d);
      return { ...f, weekdays: has ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d] };
    });
  };

  const ready = form ? toInput(form) !== null : false;
  const serviceStandard =
    form && state.kind === 'ready' ? state.serviceTypes.find((s) => s.id === form.serviceTypeId) ?? null : null;

  return (
    <article className="ferro-dashboard__card ferro-rate-agreements" aria-label="Pricing agreements" data-testid="rate-agreements-card">
      <header className="ferro-dashboard__cardHeader">
        <h2 className="ferro-dashboard__cardTitle">Pricing agreements</h2>
        {canWrite && state.kind === 'ready' && !form ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={openAdd}
            disabled={saving}
            data-testid="rate-agreements-add"
          >
            Add agreed price
          </button>
        ) : null}
      </header>
      <p className="ferro-muted">
        A standing price this client has agreed with you. It never changes the standard prices — the desk sees it at
        checkout and applies it with one tap.
      </p>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" data-testid="rate-agreements-loading" aria-busy="true">
          Loading agreed prices…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div className="ferro-form__error" role="alert" data-testid="rate-agreements-error">
          <p>Could not load agreed prices — {state.message}</p>
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={() => void load()} data-testid="rate-agreements-retry">
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' && state.agreements.length === 0 && !form ? (
        <p className="ferro-settings__teaching" data-testid="rate-agreements-empty">
          No agreed prices on file — the standard prices apply. Save one here when a client has a standing deal, e.g.
          two dogs for $70.00 on Tuesdays and Thursdays.
        </p>
      ) : null}

      {state.kind === 'ready' && state.agreements.length > 0 ? (
        <ul className="ferro-rate-agreements__list" data-testid="rate-agreements-list">
          {state.agreements.map((a) => (
            <li key={a.id} className="ferro-rate-agreements__row" data-testid={`rate-agreement-row-${a.id}`}>
              <div className="ferro-rate-agreements__row-main">
                <div className="ferro-rate-agreements__label" data-testid={`rate-agreement-label-${a.id}`}>
                  {agreementRowLabel(a)}
                </div>
                <div className="ferro-muted ferro-rate-agreements__note" data-testid={`rate-agreement-note-${a.id}`}>
                  {a.basis === 'per_dog' ? 'Per dog' : 'For the household that day'} — {a.note}
                </div>
              </div>
              {canWrite ? (
                <div className="ferro-rate-agreements__row-actions">
                  {confirmRemoveId === a.id ? (
                    <>
                      <span className="ferro-muted">Remove this agreed price?</span>
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--primary"
                        onClick={() => void handleRemove(a)}
                        disabled={saving}
                        data-testid={`rate-agreement-remove-confirm-${a.id}`}
                      >
                        {saving ? 'Removing…' : 'Remove'}
                      </button>
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost"
                        onClick={() => setConfirmRemoveId(null)}
                        disabled={saving}
                        data-testid={`rate-agreement-remove-cancel-${a.id}`}
                      >
                        Keep
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost"
                        onClick={() => openEdit(a)}
                        disabled={saving}
                        data-testid={`rate-agreement-edit-${a.id}`}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="ferro-btn ferro-btn--ghost"
                        onClick={() => setConfirmRemoveId(a.id)}
                        disabled={saving}
                        data-testid={`rate-agreement-remove-${a.id}`}
                      >
                        Remove
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {rowError ? (
        <p className="ferro-form__error" role="alert" data-testid="rate-agreements-row-error">
          {rowError}
        </p>
      ) : null}

      {state.kind === 'ready' && !canWrite ? (
        <p className="ferro-settings__readonly" data-testid="rate-agreements-readonly">
          Only the owner can add or change agreed prices.
        </p>
      ) : null}

      {form && state.kind === 'ready' ? (
        <form className="ferro-rate-agreements__form" onSubmit={(e) => void handleSubmit(e)} data-testid="rate-agreements-form">
          <label className="ferro-field">
            <span className="ferro-field__label">Service</span>
            <select
              className="ferro-field__input"
              value={form.serviceTypeId}
              onChange={(e) => setForm({ ...form, serviceTypeId: e.target.value })}
              disabled={saving}
              required
              data-testid="rate-agreement-service"
            >
              {state.serviceTypes.length === 0 ? <option value="">No services found</option> : null}
              {state.serviceTypes.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.display_name} — standard {formatMoney(s.base_price_cents)}
                </option>
              ))}
            </select>
          </label>
          <div className="ferro-rate-agreements__grid">
            <label className="ferro-field">
              <span className="ferro-field__label">Agreed price ($)</span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                className="ferro-field__input"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                disabled={saving}
                required
                data-testid="rate-agreement-price"
              />
              {serviceStandard ? (
                <span className="ferro-field__hint">Standard {formatMoney(serviceStandard.base_price_cents)}</span>
              ) : null}
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">The price is</span>
              <select
                className="ferro-field__input"
                value={form.basis}
                onChange={(e) => setForm({ ...form, basis: e.target.value as AdminRateAgreementBasis })}
                disabled={saving}
                data-testid="rate-agreement-basis"
              >
                <option value="per_household_day">{basisLabel('per_household_day')}</option>
                <option value="per_dog">{basisLabel('per_dog')}</option>
              </select>
            </label>
            <label className="ferro-field">
              <span className="ferro-field__label">Number of dogs (optional)</span>
              <input
                type="number"
                inputMode="numeric"
                min="1"
                max="20"
                step="1"
                className="ferro-field__input"
                value={form.dogCount}
                onChange={(e) => setForm({ ...form, dogCount: e.target.value })}
                disabled={saving}
                placeholder="Any"
                data-testid="rate-agreement-dog-count"
              />
              <span className="ferro-field__hint">Leave blank for any number; 2 = exactly two of their dogs that day.</span>
            </label>
          </div>
          <fieldset className="ferro-rate-agreements__weekdays" data-testid="rate-agreement-weekdays">
            <legend className="ferro-field__label">Days (leave all off for any day)</legend>
            <div className="ferro-rate-agreements__weekday-row">
              {WEEKDAY_SHORT.map((name, d) => (
                <label key={name} className="ferro-field ferro-field--inline ferro-rate-agreements__weekday">
                  <input
                    type="checkbox"
                    checked={form.weekdays.includes(d)}
                    onChange={() => toggleWeekday(d)}
                    disabled={saving}
                    data-testid={`rate-agreement-weekday-${d}`}
                  />
                  <span className="ferro-field__label">{name}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label className="ferro-field">
            <span className="ferro-field__label">Where this came from (required)</span>
            <input
              type="text"
              className="ferro-field__input"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              disabled={saving}
              maxLength={200}
              placeholder="e.g. Vicki/QuickBooks: 2 dogs Tue/Thu $70"
              required
              data-testid="rate-agreement-note"
            />
          </label>
          {formError ? (
            <p className="ferro-form__error" role="alert" data-testid="rate-agreements-form-error">
              {formError}
            </p>
          ) : null}
          <div className="ferro-settings__actions">
            <button type="submit" className="ferro-btn ferro-btn--primary" disabled={!ready || saving} data-testid="rate-agreement-save">
              {saving ? 'Saving…' : form.editingId ? 'Save changes' : 'Save agreed price'}
            </button>
            <button type="button" className="ferro-btn ferro-btn--ghost" onClick={closeForm} disabled={saving} data-testid="rate-agreement-cancel">
              Cancel
            </button>
          </div>
        </form>
      ) : null}
    </article>
  );
}
